// Map style — light, dark, or auto (dark from 6 pm to 6 am Manila).
//
// Leon, 2026-10-23: the dark map for everyone, with a setting in each app.
// It changes the MAP only, never the rest of the app: client and staff have no
// full dark mode yet. Saved per device (localStorage), so it needs no account
// change and works signed out. Every ServiceMap reads it, so client, staff and
// admin behave the same.
//
// "Auto" is the default: daytime maps stay readable in sunlight (riders), and
// it is the rule rider Drive used before (night 6 pm – 6 am, Manila).

import { useEffect, useState } from "react";

export type MapStyleChoice = "auto" | "light" | "dark";

export const MAP_STYLE_CHOICES: readonly { value: MapStyleChoice; label: string; hint: string }[] = [
  { value: "auto", label: "Auto", hint: "Dark from 6 pm to 6 am" },
  { value: "light", label: "Light", hint: "Always light" },
  { value: "dark", label: "Dark", hint: "Always dark" },
];

const STORAGE_KEY = "bnb-map-style";
const CHANGE_EVENT = "bnb-map-style-change";

function isChoice(value: unknown): value is MapStyleChoice {
  return value === "auto" || value === "light" || value === "dark";
}

/** Storage can throw (private windows, blocked site data) — fall back to auto. */
export function readMapStyle(): MapStyleChoice {
  try {
    const stored = typeof window === "undefined" ? null : window.localStorage.getItem(STORAGE_KEY);
    return isChoice(stored) ? stored : "auto";
  } catch {
    return "auto";
  }
}

export function writeMapStyle(choice: MapStyleChoice): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    // Not saved, but this session still switches below.
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: choice }));
}

/** 6 pm to 6 am in Manila (UTC+8, no DST since 1978). */
export function isNightInManila(atMs: number): boolean {
  const hour = new Date(atMs + 8 * 3_600_000).getUTCHours();
  return hour >= 18 || hour < 6;
}

export function mapIsDark(choice: MapStyleChoice, atMs: number): boolean {
  if (choice === "dark") return true;
  if (choice === "light") return false;
  return isNightInManila(atMs);
}

/** The person's choice, kept in step across every map and settings screen. */
export function useMapStyleChoice(): [MapStyleChoice, (choice: MapStyleChoice) => void] {
  const [choice, setChoice] = useState<MapStyleChoice>(readMapStyle);
  useEffect(() => {
    const onChange = () => setChoice(readMapStyle());
    window.addEventListener(CHANGE_EVENT, onChange);
    // Another tab of the same app.
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);
  return [choice, writeMapStyle];
}

/** Whether maps draw dark right now; on Auto it re-checks the clock each minute. */
export function useMapDark(): boolean {
  const [choice] = useMapStyleChoice();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (choice !== "auto") return;
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, [choice]);
  return mapIsDark(choice, now);
}
